import React, {
  ComponentProps,
  ComponentPropsWithRef,
  ReactElement,
  Ref,
} from 'react';

import PropTypes from 'prop-types';
import { CSSObject } from 'styled-components';

import Button from '@adsk/alloy-react-button';
import {
  pickKeys,
  optionalPropType,
  useForwardedRef,
} from '@adsk/alloy-react-helpers';
import Overlay from '@adsk/alloy-react-overlay';
import { StyleProp, stylePropType } from '@adsk/alloy-react-theme';

import defaultControlRenderer, {
  type DefaultControlRendererFn,
} from './DefaultControlRenderer';
import { menuCategoryShape, menuItemShape } from '../../helpers/propTypes';
import {
  MenuCategoryType,
  MenuItemType,
  SharedProps,
} from '../../helpers/types';
import useMenuButton from '../../hooks/useMenuButton';
import Menu from '../../Menu';

type OverlayComponentProps = ComponentProps<typeof Overlay>;

type ExplicitOverlayProps = Pick<
  OverlayComponentProps,
  'container' | 'placement' | 'show' | 'onShow' | 'onHide' | 'overlayRef'
>;

type DropdownButtonProps<TKey extends string | number> = Omit<
  ComponentProps<typeof Button>,
  'defaultValue' | 'value' | 'onChange' | 'type'
> &
  ExplicitOverlayProps &
  SharedProps<TKey> & {
    horizontal?: boolean;
    options?: (MenuItemType<TKey> | MenuCategoryType<TKey>)[];
    renderContent?: (
      props: ComponentPropsWithRef<typeof Menu<TKey>>,
    ) => ReactElement;
    renderControl?: DefaultControlRendererFn;
    menuStyle?: StyleProp;
    /** Additional props forwarded to the underlying Overlay component. Explicit overlay props (container, placement, show, onShow, onHide, overlayRef) take precedence. */
    overlayProps?: Omit<
      Partial<OverlayComponentProps>,
      keyof ExplicitOverlayProps | 'renderTarget' | 'renderOverlay' | 'target'
    >;
  };
/**
 * Dropdown Button component with Menu
 * A single control that opens a menu of related actions.
 */
const DropdownButtonWrapped = <TKey extends string | number>(
  {
    value,
    options = [],
    onChange,
    renderContent = (p) => <Menu {...p} />,
    renderControl = defaultControlRenderer,
    renderFooter,
    defaultValue = [],
    show: showProp,
    onShow,
    onHide,
    placement: placementProp = Overlay.PLACEMENTS.BOTTOM_END,
    container = document.body,
    variant = Button.VARIANTS.SECONDARY,
    menuStyle = { minWidth: 200 },
    overlayRef,
    overlayProps: overlayPropsProp,
    ...props
  }: DropdownButtonProps<TKey>,
  ref: Ref<HTMLButtonElement>,
) => {
  const targetRef = useForwardedRef(ref);

  const { getControlProps, getMenuProps } = useMenuButton({
    defaultValue,
    value,
    onChange,
  });

  return (
    <Overlay<HTMLButtonElement>
      {...overlayPropsProp}
      show={showProp}
      onShow={onShow}
      onHide={onHide}
      target={targetRef}
      overlayRef={overlayRef}
      placement={placementProp}
      container={container}
      renderTarget={(buttonProps) => {
        const controlProps = {
          variant,
          ...getControlProps({ show: buttonProps.show }),
          ...buttonProps,
          ...props,
        };

        return renderControl?.(controlProps);
      }}
      renderOverlay={({ overlayProps, placement, handleHide }) =>
        renderContent({
          'data-testid': 'Dropdown__Menu',
          ref: overlayProps.ref,
          placement,
          options,
          defaultValue,
          onClose: handleHide,
          style: {
            ...(overlayProps.style as CSSObject),
            ...{ width: targetRef?.current?.offsetWidth },
            ...(menuStyle as CSSObject),
          },
          renderFooter,
          ...getMenuProps(),
        })
      }
    />
  );
};

const DropdownButton = React.forwardRef(DropdownButtonWrapped);

DropdownButton.displayName = 'DropdownButton';

DropdownButton.propTypes = {
  ...Button.propTypes,
  ...pickKeys<Record<string, unknown>>(
    Overlay.propTypes as Record<string, unknown>,
    ['container', 'placement', 'show', 'onShow', 'onHide', 'overlayRef'],
  ),
  /** Options for the action */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  options: PropTypes.arrayOf<any>(
    PropTypes.oneOfType([menuItemShape, menuCategoryShape]),
  ),
  /** Pre selected keys */
  defaultValue: optionalPropType(
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    ),
  ),
  /** Value for controlled usage */
  value: optionalPropType(
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    ),
  ),
  /** Handle for item click event */
  onChange: PropTypes.func,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** DropdownButton's content */
  children: PropTypes.any,
  /** Disable button functionality */
  disabled: PropTypes.bool,
  /** handle onClick event for button */
  onClick: PropTypes.func,
  /** render custom menu content, called with args ({ ref, placement, options, defaultValue, value, onChange, onClose, style }) */
  renderContent: PropTypes.func,
  /** render custom control (button/tag/etc), called with args ({ targetProps, show, handleToggle, disabled, variant, ...buttonProps }) */
  renderControl: PropTypes.func,
  /** Styles applied to the overlay element */
  menuStyle: stylePropType,
  /** Additional props forwarded to the underlying Overlay component */
  overlayProps: PropTypes.object,
};

export default Object.assign(
  DropdownButton as <TKey extends string | number>(
    p: DropdownButtonProps<TKey> & { ref?: Ref<HTMLDivElement> },
  ) => ReactElement,
  {
    VARIANTS: Button.VARIANTS,
    SIZES: Button.SIZES,
    PLACEMENTS: Overlay.PLACEMENTS,
  },
);
